import { useState } from 'react';
import { useOrderPolling } from '../../hooks/useOrderPolling';
import { acceptOrder, completeOrder, pickedUpOrder, rejectOrder } from '../../api/appmanageOrders';
import { Order } from '../../types/admin';
import './OrderManagementScreen.scss';

const ESTIMATED_MINUTE_PRESETS = [5, 10, 15, 20];

const PAYMENT_METHOD_LABEL: Record<Order['payment_method'], string> = {
  card: '카드',
  kakao_pay: '카카오페이',
  toss_pay: '토스페이',
};

function OrderCard({ order, onChanged }: { order: Order; onChanged: () => void }) {
  const [selectedMinutes, setSelectedMinutes] = useState(10);

  const handleAccept = async () => {
    await acceptOrder(order.id, selectedMinutes);
    onChanged();
  };

  const handleReject = async () => {
    await rejectOrder(order.id);
    onChanged();
  };

  const handleComplete = async () => {
    await completeOrder(order.id);
    onChanged();
  };

  const handlePickedUp = async () => {
    await pickedUpOrder(order.id);
    onChanged();
  };

  return (
    <div className="OrderCard">
      <div className="OrderCard-header">
        <span className="OrderCard-number">{order.order_number}</span>
        <span className={`OrderCard-type OrderCard-type--${order.order_type}`}>
          {order.order_type === 'take_out' ? '포장' : '매장'}
        </span>
      </div>

      <ul className="OrderCard-items">
        {order.items.map((item) => (
          <li key={item.id}>
            {item.menu_name} x{item.quantity}
          </li>
        ))}
      </ul>

      <div className="OrderCard-footer">
        <span>{order.total_amount.toLocaleString()}원</span>
        <span>{PAYMENT_METHOD_LABEL[order.payment_method]}</span>
      </div>

      {order.status === 'pending' && (
        <div className="OrderCard-actions">
          <div className="OrderCard-minutes">
            {ESTIMATED_MINUTE_PRESETS.map((minutes) => (
              <button
                key={minutes}
                type="button"
                className={selectedMinutes === minutes ? 'active' : ''}
                onClick={() => setSelectedMinutes(minutes)}
              >
                {minutes}분
              </button>
            ))}
          </div>
          <div className="OrderCard-buttons">
            <button type="button" className="accept" onClick={handleAccept}>
              수락
            </button>
            <button type="button" className="reject" onClick={handleReject}>
              거절
            </button>
          </div>
        </div>
      )}

      {order.status === 'preparing' && (
        <div className="OrderCard-actions">
          <p className="OrderCard-eta">예상 제조 시간: {order.estimated_minutes}분</p>
          <button type="button" className="complete" onClick={handleComplete}>
            제조 완료
          </button>
        </div>
      )}

      {order.status === 'completed' && (
        <div className="OrderCard-actions">
          <p className="OrderCard-done">제조 완료됨 — 픽업 대기중</p>
          <button type="button" className="complete" onClick={handlePickedUp}>
            수령 완료
          </button>
        </div>
      )}
    </div>
  );
}

function OrderColumn({ title, orders, onChanged }: { title: string; orders: Order[]; onChanged: () => void }) {
  return (
    <section className="OrderColumn">
      <h2>
        {title} <span className="OrderColumn-count">{orders.length}</span>
      </h2>
      <div className="OrderColumn-list">
        {orders.length === 0 && <p className="OrderColumn-empty">주문이 없습니다</p>}
        {orders.map((order) => (
          <OrderCard key={order.id} order={order} onChanged={onChanged} />
        ))}
      </div>
    </section>
  );
}

function OrderManagementScreen() {
  const { orders, loading, refetch } = useOrderPolling();

  const pending = orders.filter((o) => o.status === 'pending');
  const preparing = orders.filter((o) => o.status === 'preparing');
  const completed = orders.filter((o) => o.status === 'completed');

  return (
    <div className="OrderManagementScreen">
      <h1>실시간 주문 관리</h1>
      {loading ? (
        <p>불러오는 중...</p>
      ) : (
        <div className="OrderManagementScreen-columns">
          <OrderColumn title="신규 주문" orders={pending} onChanged={refetch} />
          <OrderColumn title="제조 중" orders={preparing} onChanged={refetch} />
          <OrderColumn title="제조 완료" orders={completed} onChanged={refetch} />
        </div>
      )}
    </div>
  );
}

export default OrderManagementScreen;
